import { useState, useMemo, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
} from "../../store/slices/authSlice";
import {
  useUpdateAppointmentMutation,
  useCancelAppointmentMutation,
} from "../../store/services/calendarApi";
import { useCreatePatientMutation } from "../../store/services/patientsApi";
import { sendBookingDecision } from "../../firebase";
import { useToast } from "../../contexts/ToastContext";
import { formatHebrewDate, formatTime } from "../../utils/dateUtils";
import { toLocalPhoneIL } from "../../utils/phoneUtils";
import ModalCloseButton from "../ui/ModalCloseButton";
import {
  CalendarBlank,
  Clock,
  Phone,
  User,
  UserPlus,
  CheckCircle,
  XCircle,
  Stethoscope,
} from "@phosphor-icons/react";
import LoaderLoop from "../shared/loader/LoaderLoop";
import "./BookingApprovalModal.css";

// Approval modal for a patient self-booked appointment that is pending the
// therapist's decision. The therapist must match a patient (existing or quick-add)
// before approving; rejecting cancels the request and frees the slot.
function BookingApprovalModal({ appointment, patients = [], onClose, onDecided }) {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const { showSuccess, showError } = useToast();

  const [updateAppointment] = useUpdateAppointmentMutation();
  const [cancelAppointment] = useCancelAppointmentMutation();
  const [createPatient, { isLoading: isCreatingPatient }] =
    useCreatePatientMutation();

  const [selectedPatientId, setSelectedPatientId] = useState("");
  // Patients quick-added inside this modal, merged on top of the prop list
  // (which can still be loading when the modal opens).
  const [addedPatients, setAddedPatients] = useState([]);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);

  const bookerName =
    appointment?.bookedByName || appointment?.patientName || "";
  // Booking stores +972…; patient docs store local 05X… — normalize on the way in.
  const bookerPhone = toLocalPhoneIL(
    appointment?.bookedByPhone || appointment?.patientPhone || ""
  );

  // Quick-add form, prefilled from the booker's details.
  const splitName = useMemo(() => {
    const parts = String(bookerName).trim().split(/\s+/);
    return {
      first: parts[0] || "",
      last: parts.slice(1).join(" ") || "",
    };
  }, [bookerName]);
  const [qaFirst, setQaFirst] = useState(splitName.first);
  const [qaLast, setQaLast] = useState(splitName.last);
  const [qaPhone, setQaPhone] = useState(bookerPhone);

  const startDate = appointment?.startTime;
  const dateLabel = startDate ? formatHebrewDate(startDate) : "";
  const timeLabel = startDate ? formatTime(startDate) : "";

  const localPatients = useMemo(() => {
    const seen = new Set();
    return [...addedPatients, ...patients].filter((p) => {
      if (!p?.id || seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [addedPatients, patients]);

  const selectedPatient = localPatients.find((p) => p.id === selectedPatientId);

  // Pre-select the first patient whose phone matches the booker's phone. Runs
  // once the patient list is available and only while nothing is selected yet,
  // so it never overrides the therapist's own choice.
  const autoMatchDone = useRef(false);
  useEffect(() => {
    if (autoMatchDone.current || selectedPatientId) return;
    const normalized = toLocalPhoneIL(bookerPhone);
    if (normalized.length < 9 || localPatients.length === 0) return;
    const match = localPatients.find(
      (p) =>
        toLocalPhoneIL(p.phone) === normalized ||
        (p.isCouple === true &&
          (toLocalPhoneIL(p.partner1?.phone) === normalized ||
            toLocalPhoneIL(p.partner2?.phone) === normalized))
    );
    if (match) {
      autoMatchDone.current = true;
      setSelectedPatientId(match.id);
    }
  }, [localPatients, bookerPhone, selectedPatientId]);

  const handleQuickAdd = async () => {
    setError("");
    if (qaFirst.trim().length < 1) {
      setError("נא להזין שם פרטי");
      return;
    }
    try {
      const patient = await createPatient({
        clinicId,
        patientData: {
          firstName: qaFirst.trim(),
          lastName: qaLast.trim() || "-",
          phone: toLocalPhoneIL(qaPhone.trim()),
          referralReason: "הוזמן דרך קישור הזמנה עצמית",
          status: "active",
          isActive: true,
        },
        therapistId,
        therapistName,
      }).unwrap();
      setAddedPatients((prev) => [patient, ...prev]);
      setSelectedPatientId(patient.id);
      setShowQuickAdd(false);
      showSuccess("המטופל נוצר ושויך לתור");
    } catch (e) {
      console.error("[CK-3011] Error quick-adding patient on approval:", {
        errorCode: "CK-3011",
        errorMessage: e.message,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה ביצירת המטופל");
    }
  };

  const handleApprove = async () => {
    setError("");
    if (!selectedPatientId) {
      setError("יש לבחור מטופל לפני אישור התור");
      return;
    }
    const patientName = selectedPatient
      ? `${selectedPatient.firstName} ${selectedPatient.lastName}`.trim()
      : bookerName;
    // Self-booked appointments are written server-side WITHOUT a financial
    // transaction. The update mutation only creates one when paymentAmount is
    // part of the payload, so resolve it here: patient.treatmentRate wins,
    // otherwise the server-resolved rate (service rate → clinic default).
    const patientRate = Number(selectedPatient?.treatmentRate);
    const resolvedAmount =
      Number.isFinite(patientRate) && patientRate > 0
        ? patientRate
        : Number(appointment.paymentAmount) || null;
    setWorking(true);
    try {
      await updateAppointment({
        clinicId,
        appointmentId: appointment.id,
        appointmentData: {
          patientId: selectedPatientId,
          patientName,
          status: "scheduled",
          pendingApproval: false,
          approvedBy: therapistId,
          approvedAt: new Date().toISOString(),
          ...(resolvedAmount > 0 ? { paymentAmount: resolvedAmount } : {}),
        },
        therapistId,
        therapistName,
        patientName,
      }).unwrap();
      // SMS the booker (best-effort).
      sendBookingDecision(clinicId, appointment.id, true);
      showSuccess("התור אושר ושויך למטופל");
      onDecided?.();
      onClose?.();
    } catch (e) {
      console.error("[CK-3012] Error approving booking:", {
        errorCode: "CK-3012",
        errorMessage: e.message,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה באישור התור");
    } finally {
      setWorking(false);
    }
  };

  const handleReject = async () => {
    if (!window.confirm("לדחות את בקשת התור? פעולה זו תפנה את המועד.")) return;
    setWorking(true);
    try {
      await cancelAppointment({
        clinicId,
        appointmentId: appointment.id,
        cancelledBy: "therapist",
        cancellationReason: 'נדחה ע"י המטפל',
        therapistId,
        therapistName,
        patientName: bookerName,
      }).unwrap();
      sendBookingDecision(clinicId, appointment.id, false);
      showSuccess("בקשת התור נדחתה");
      onDecided?.();
      onClose?.();
    } catch (e) {
      console.error("[CK-3013] Error rejecting booking:", {
        errorCode: "CK-3013",
        errorMessage: e.message,
        timestamp: new Date().toISOString(),
      });
      showError("שגיאה בדחיית התור");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="bam-overlay" onClick={onClose}>
      <div className="bam-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bam-header">
          <h2>בקשת תור לאישור</h2>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="bam-body">
          {/* Booker + requested slot */}
          <div className="bam-summary">
            <div className="bam-summary-row">
              <User size={18} weight="fill" />
              <span>{bookerName}</span>
            </div>
            {bookerPhone && (
              <div className="bam-summary-row">
                <Phone size={18} weight="fill" />
                <span dir="ltr">{bookerPhone}</span>
              </div>
            )}
            <div className="bam-summary-row">
              <CalendarBlank size={18} weight="fill" />
              <span>{dateLabel}</span>
            </div>
            <div className="bam-summary-row">
              <Clock size={18} weight="fill" />
              <span>{timeLabel}</span>
            </div>
            {appointment?.treatmentTypeName && (
              <div className="bam-summary-row">
                <Stethoscope size={18} weight="fill" />
                <span>
                  {appointment.treatmentTypeName}
                  {appointment.duration ? ` · ${appointment.duration} דק׳` : ""}
                </span>
              </div>
            )}
          </div>

          {/* Patient match */}
          <div className="bam-field">
            <label className="bam-label">שיוך למטופל</label>
            {!showQuickAdd ? (
              <div className="bam-select-row">
                <select
                  className="bam-select"
                  value={selectedPatientId}
                  onChange={(e) => {
                    autoMatchDone.current = true;
                    setSelectedPatientId(e.target.value);
                    setError("");
                  }}
                >
                  <option value="">בחר מטופל מהרשימה...</option>
                  {localPatients.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="bam-add-btn"
                  onClick={() => setShowQuickAdd(true)}
                  title="הוספת מטופל חדש"
                >
                  <UserPlus size={18} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="bam-quickadd">
                <div className="bam-quickadd-row">
                  <input
                    className="bam-input"
                    placeholder="שם פרטי"
                    value={qaFirst}
                    onChange={(e) => setQaFirst(e.target.value)}
                  />
                  <input
                    className="bam-input"
                    placeholder="שם משפחה"
                    value={qaLast}
                    onChange={(e) => setQaLast(e.target.value)}
                  />
                </div>
                <input
                  className="bam-input"
                  placeholder="טלפון"
                  type="tel"
                  inputMode="numeric"
                  value={qaPhone}
                  onChange={(e) => setQaPhone(e.target.value)}
                />
                <div className="bam-quickadd-actions">
                  <button
                    type="button"
                    className="bam-btn-ghost"
                    onClick={() => setShowQuickAdd(false)}
                  >
                    ביטול
                  </button>
                  <button
                    type="button"
                    className="bam-btn-secondary"
                    onClick={handleQuickAdd}
                    disabled={isCreatingPatient}
                  >
                    {isCreatingPatient ? (
                      <LoaderLoop size="xs" />
                    ) : (
                      "צור ושייך"
                    )}
                  </button>
                </div>
              </div>
            )}
            {selectedPatient && !showQuickAdd && (
              <p className="bam-matched">
                <CheckCircle size={15} weight="fill" /> ישויך:{" "}
                {selectedPatient.firstName} {selectedPatient.lastName}
              </p>
            )}
          </div>

          {error && <p className="bam-error">{error}</p>}
        </div>

        <div className="bam-footer">
          <button
            type="button"
            className="bam-btn-reject"
            onClick={handleReject}
            disabled={working}
          >
            <XCircle size={18} weight="fill" />
            <span>דחייה</span>
          </button>
          <button
            type="button"
            className="bam-btn-approve"
            onClick={handleApprove}
            disabled={working || !selectedPatientId}
          >
            {working ? (
              <LoaderLoop size="xs" />
            ) : (
              <>
                <CheckCircle size={18} weight="fill" />
                <span>אישור התור</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BookingApprovalModal;
